import { useEffect, useState } from "react";
import { Flame, Fish, Sparkles, LayoutGrid, Radio, Cherry, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { SlotModal } from "@/components/SlotModal";
import { PlinkoModal } from "@/components/games/PlinkoModal";
import { MinesModal } from "@/components/games/MinesModal";
import { BlackjackModal } from "@/components/games/BlackjackModal";
import { HeroCarousel, PromoTiles } from "@/components/lobby/Hero";
import { JackpotTicker } from "@/components/lobby/JackpotTicker";
import { GameCard, GameRow } from "@/components/lobby/GameRow";

const CATS = [["all", "Lobby", LayoutGrid], ["slots", "Slots", Cherry], ["live", "Live Casino", Radio], ["originals", "Originals", Sparkles], ["fish", "Fish", Fish]];

export default function Lobby() {
  const { setUser, openStore } = useApp();
  const [games, setGames] = useState([]);
  const [cat, setCat] = useState("all");
  const [playing, setPlaying] = useState(null);
  useEffect(() => { api.get("/games").then((r) => setGames(r.data)); }, []);
  const by = (c) => games.filter((g) => g.category === c);
  const engine = playing?.engine;

  const claim = async () => {
    try { const { data } = await api.post("/bonus/daily"); setUser(data); toast.success("Daily bonus claimed: 5,000 GC + 0.50 SC"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const heroCta = (id) => (id === "welcome" ? openStore("buy") : setCat(id === "live" ? "live" : "slots"));
  const close = () => setPlaying(null);

  return (
    <main className="relative z-10 max-w-7xl mx-auto px-4 pb-28 md:pb-20" data-testid="lobby-page">
      <HeroCarousel games={games} onCta={heroCta} onDaily={claim} />
      <PromoTiles onDaily={claim} />
      <JackpotTicker />

      <div className="sticky top-16 z-30 -mx-4 px-4 py-3 mt-6 bg-[#0B0E14]/85 backdrop-blur-xl flex gap-2 overflow-x-auto no-scrollbar" data-testid="category-tabs">
        {CATS.map(([k, l, I]) => (
          <button key={k} data-testid={`category-${k}`} onClick={() => setCat(k)} className={`shrink-0 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold border transition-colors ${cat === k ? "bg-[#FFB800] border-[#FFB800] text-[#0B0E14]" : "bg-[#151A22] border-white/5 text-gray-400 hover:text-white"}`}><I size={16} />{l}</button>
        ))}
      </div>

      {cat === "all" ? (
        <>
          <GameRow id="hot" title="Hot Right Now" icon={Flame} games={games.filter((g) => g.badge === "HOT")} onPlay={setPlaying} />
          <GameRow id="new" title="New Releases" icon={Star} games={games.filter((g) => g.badge === "NEW")} onPlay={setPlaying} />
          <GameRow id="live" title="Live Casino" icon={Radio} games={by("live")} onPlay={setPlaying} onSeeAll={() => setCat("live")} />
          <GameRow id="originals" title="Originals" icon={Sparkles} games={by("originals")} onPlay={setPlaying} onSeeAll={() => setCat("originals")} />
          <GameRow id="slots" title="Slots" icon={Cherry} games={by("slots")} onPlay={setPlaying} onSeeAll={() => setCat("slots")} />
          <section className="mt-10" data-testid="section-all-games">
            <h2 className="flex items-center gap-2 font-display text-lg sm:text-xl font-black uppercase"><LayoutGrid size={20} className="gc-text" />All Games <span className="ml-1 rounded-md bg-[#1F2633] px-2 py-0.5 text-[11px] text-gray-300">{games.length}</span></h2>
            <div className="mt-3 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="games-grid">
              {games.map((g, i) => <GameCard key={g.id} g={g} i={i} onPlay={setPlaying} />)}
            </div>
          </section>
        </>
      ) : (
        <div className="mt-4 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="games-grid">
          {by(cat).map((g, i) => <GameCard key={g.id} g={g} i={i} onPlay={setPlaying} />)}
        </div>
      )}

      <p className="mt-12 text-xs text-gray-600 max-w-2xl">No purchase necessary to play. Gold Coins have no monetary value. Sweeps Coins may be redeemed for prizes subject to the Sweepstakes Rules. Jackpot displays are for entertainment. Void where prohibited. 18+.</p>
      <SlotModal game={engine === "slots" ? playing : null} onClose={close} />
      <PlinkoModal game={engine === "plinko" ? playing : null} onClose={close} />
      <MinesModal game={engine === "mines" ? playing : null} onClose={close} />
      <BlackjackModal game={engine === "blackjack" ? playing : null} onClose={close} />
    </main>
  );
}
